import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  Flex,
  useToast,
  Link,
  IconButton,
  Spinner,
  Icon,
  Circle,
} from '@chakra-ui/react';
import { CopyIcon, QuestionIcon, CheckIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { FaInstagram } from 'react-icons/fa';
import { createOrRetrieveInstagramVerificationCode, checkInstagramVerification } from 'services/firebaseService';
import { useInstagramVerification } from 'contexts/InstagramVerificationContext';
import { normalizeInstagramHandle } from 'services/verificationService';
import { DisplayHeading, Pill } from '../../../components/ui';
import { ff } from '../../../theme';

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <Circle size="28px" bg={ff.ink} color={ff.lime} fontSize="sm" fontWeight={600} mr={3} flexShrink={0}>
    {n}
  </Circle>
);

interface InstagramVerificationStepProps {
  email: string;
  instagram: string;
  /**
   * Pre-created code, passed by the handle-change flow. When present the proof record was
   * already created server-side and stamped with the creator's ID, so this component must not
   * create one itself — and must not treat an existing verified record as proof, since that
   * record may belong to whoever held the handle before.
   */
  verificationCode?: string;
  /** Only proof stamped with this creator's ID counts. Signup omits it. */
  expectedInfluencerID?: string;
  onVerificationSuccess: () => void;
  onBack: () => void;
  isUserAuthenticated?: boolean;
}

const InstagramVerificationStep: React.FC<InstagramVerificationStepProps> = ({
  email,
  instagram,
  verificationCode,
  expectedInfluencerID,
  onVerificationSuccess,
  onBack,
  isUserAuthenticated = false,
}) => {
  const [instagramVerificationCode, setInstagramVerificationCode] = useState(verificationCode || '');
  const [isLoadingCode, setIsLoadingCode] = useState(!verificationCode);
  const [isCrispOpen, setIsCrispOpen] = useState(false);
  const [isAlreadyVerified, setIsAlreadyVerified] = useState(false);
  const toast = useToast();
  const { isVerified, hasOwnershipConflict, watchInstagramHandle, currentHandle } = useInstagramVerification();
  // The provider lives at app root, so on this component's first render `isVerified` may still
  // describe the previous handle — its watch effect has not run yet. Only trust it for this one.
  const isVerifiedForThisHandle = isVerified
    && !!currentHandle
    && normalizeInstagramHandle(currentHandle) === normalizeInstagramHandle(instagram);

  useEffect(() => {
    if (instagram) {
      watchInstagramHandle(instagram, expectedInfluencerID);
    }
  }, [instagram, expectedInfluencerID, watchInstagramHandle]);

  useEffect(() => {
    const initializeVerification = async () => {
      // A handle change arrives with its code already created server-side. Creating another
      // here would reset the record the creator is about to DM against.
      if (verificationCode) {
        return;
      }
      if (email && instagram) {
        try {
          setIsLoadingCode(true);
          const isAlreadyVerified = await checkInstagramVerification(instagram);
          if (isAlreadyVerified) {
            setIsAlreadyVerified(true);
            setIsLoadingCode(false);
            return;
          }

          const code = await createOrRetrieveInstagramVerificationCode(email, instagram);
          setInstagramVerificationCode(code);
        } catch (error) {
          console.error('Error setting up verification:', error);
          toast({
            title: "Error",
            description: "Failed to generate verification code. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsLoadingCode(false);
        }
      }
    };

    initializeVerification();
  }, [email, instagram, verificationCode, onVerificationSuccess]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(instagramVerificationCode);
      toast({
        title: "Copied!",
        description: "Verification code copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Please copy the code manually",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleCrisp = () => {
    if (typeof (window as any).$crisp !== 'undefined') {
      if (isCrispOpen) {
        (window as any).$crisp.push(['do', 'chat:close']);
        (window as any).$crisp.push(['do', 'chat:hide']);
      } else {
        (window as any).$crisp.push(['do', 'chat:show']);
        (window as any).$crisp.push(['do', 'chat:open']);
      }
      setIsCrispOpen(!isCrispOpen);
    } else {
      toast({
        title: "Help unavailable",
        description: "Support chat is currently unavailable. Please try again later.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isAlreadyVerified || isVerifiedForThisHandle) {
    return (
      <VStack spacing={6} align="stretch" width="100%" flex={1} pt={6}>
        <Circle size="64px" bg={ff.lime}>
          <Icon as={CheckIcon} color={ff.ink} boxSize="28px" />
        </Circle>
        <VStack spacing={2} align="stretch">
          <Box><Pill tone="ink">Verified</Pill></Box>
          <DisplayHeading size="32px">Instagram verified!</DisplayHeading>
          <Text fontSize="15px" color={ff.text2}>
            Your Instagram account <Text as="span" fontWeight={600} color={ff.ink}>@{instagram}</Text> is now verified and ready to use.
          </Text>
        </VStack>
        <Flex
          align="center"
          gap={3}
          p={4}
          bg={ff.limeSurface}
          border="1px solid"
          borderColor={ff.line}
          borderRadius="20px"
        >
          <Icon as={CheckIcon} color={ff.ink} boxSize={4} />
          <Text fontSize="15px" fontWeight={600} color={ff.ink}>
            Verification complete
          </Text>
        </Flex>
        <Box flex={1} />
        <Button
          variant="lime"
          width="full"
          onClick={isUserAuthenticated ? onBack : onVerificationSuccess}
        >
          {isUserAuthenticated ? "Continue" : "Continue to phone verification"}
        </Button>
      </VStack>
    );
  }

  /**
   * Someone else's verification now owns this handle's record, so the code above can never verify.
   * Only reachable for a handle change (signup passes no expected creator id), and only when a
   * signup claiming the same handle overwrote the reservation mid-flight.
   */
  if (hasOwnershipConflict) {
    return (
      <VStack spacing={6} align="stretch" width="100%" flex={1} pt={6}>
        <Circle size="64px" bg={ff.grey}>
          <Icon as={FaInstagram} color={ff.ink} boxSize="28px" />
        </Circle>
        <DisplayHeading size="32px">This code is no longer valid</DisplayHeading>
        <Box p={4} bg={ff.cream} border="1px solid" borderColor={ff.line} borderRadius="20px">
          <Text fontSize="15px" fontWeight={600} color={ff.ink}>Someone else started verifying @{instagram}</Text>
          <Text fontSize="sm" color={ff.body} mt={1}>
            Your handle has not changed. Go back and start the change again to get a new code.
          </Text>
        </Box>
        <Box flex={1} />
        <Button variant="ink" width="full" onClick={onBack}>
          Back to profile
        </Button>
      </VStack>
    );
  }

  if (isLoadingCode) {
    return (
      <VStack spacing={4} align="center" justify="center" minH="400px" textAlign="center">
        <Spinner color={ff.ink} thickness="3px" size="lg" />
        <VStack spacing={1}>
          <Text fontSize="17px" fontWeight={600} color={ff.ink}>
            Setting up verification
          </Text>
          <Text fontSize="sm" color={ff.text2}>
            Generating your unique verification code...
          </Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={8} align="stretch" width="100%" pt={6}>
      {/* Header */}
      <VStack spacing={2} align="stretch">
        <Text textStyle="eyebrow" color={ff.text2}>Verify Instagram</Text>
        <DisplayHeading size="32px">Confirm your handle</DisplayHeading>
        <Text fontSize="15px" color={ff.text2}>
          We need to confirm you own <Text as="span" fontWeight={600} color={ff.ink}>@{instagram}</Text> before proceeding
        </Text>
      </VStack>

      {/* Instructions */}
      <VStack spacing={4} align="stretch">
        {/* Step 1 */}
        <Box p={5} border="1px solid" borderColor={ff.line} borderRadius="20px">
          <Flex alignItems="center" mb={3}>
            <StepNumber n={1} />
            <Text fontWeight={600} fontSize="17px" color={ff.ink}>Copy your code</Text>
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            pl={4}
            pr={1}
            minH="56px"
            bg={ff.grey}
            borderRadius="14px"
          >
            <Text
              fontFamily="mono"
              fontSize="xl"
              fontWeight={600}
              color={ff.ink}
              letterSpacing="3px"
            >
              {instagramVerificationCode}
            </Text>
            <IconButton
              aria-label="Copy code"
              icon={<CopyIcon />}
              variant="ghost"
              onClick={copyCode}
              color={ff.ink}
              minW="44px"
              h="44px"
              _hover={{ bg: ff.card }}
            />
          </Flex>
        </Box>

        {/* Step 2 */}
        <Box p={5} border="1px solid" borderColor={ff.line} borderRadius="20px">
          <Flex alignItems="center" mb={2}>
            <StepNumber n={2} />
            <Text fontWeight={600} fontSize="17px" color={ff.ink}>Send via Instagram</Text>
          </Flex>
          <Text mb={4} fontSize="15px" color={ff.text2}>
            Message the code to our Instagram account from <Text as="span" color={ff.ink} fontWeight={600}>@{instagram}</Text>
          </Text>
          <Button
            as={Link}
            href="https://www.instagram.com/direct/t/17844327537249335"
            isExternal
            leftIcon={<Icon as={FaInstagram} />}
            rightIcon={<ExternalLinkIcon />}
            variant="inkOutline"
            width="full"
            _hover={{ bg: ff.grey, textDecoration: 'none' }}
          >
            Message @foodfluence.io
          </Button>
        </Box>

        {/* Step 3 */}
        <Box p={5} border="1px solid" borderColor={ff.line} borderRadius="20px">
          <Flex alignItems="center" mb={2}>
            <StepNumber n={3} />
            <Text fontWeight={600} fontSize="17px" color={ff.ink}>Return to site</Text>
          </Flex>
          <Text fontSize="15px" fontWeight={600} color={ff.ink}>
            We'll detect verification automatically
          </Text>
          <Text fontSize="sm" color={ff.text2} mt={1}>
            Keep this page open - you'll see buttons to continue once verified
          </Text>
        </Box>
      </VStack>

      {/* Action Buttons */}
      <Flex gap={3} width="100%" direction={{ base: 'column', sm: 'row' }}>
        <Button
          variant="inkOutline"
          flex={1}
          onClick={onVerificationSuccess}
        >
          Verify later
        </Button>
        <Button
          leftIcon={<QuestionIcon />}
          variant="inkOutline"
          flex={1}
          onClick={toggleCrisp}
        >
          Need help?
        </Button>
      </Flex>
    </VStack>
  );
};

export default InstagramVerificationStep;
